import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, RefreshCw, Wallet, CheckCircle2, Clock, XCircle, PlayCircle,
} from "lucide-react";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;
const fmtMoney = (v) => (Number(v) || 0).toLocaleString("en-AU",
  { style: "currency", currency: "AUD" });
const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");

const STATUS_TONE = {
  completed: "emerald", paid: "emerald", processed: "emerald",
  submitted: "sky", inprogress: "sky", processing: "sky",
  pending: "amber", awaitingapproval: "amber", new: "zinc",
  failed: "red", rejected: "red", cancelled: "zinc",
};
const statusTone = (s) => STATUS_TONE[String(s || "").toLowerCase()
  .replace(/[^a-z]/g, "")] || "zinc";

function StatusBadge({ status }) {
  const tone = statusTone(status);
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    zinc: "bg-zinc-100 text-zinc-700 border-zinc-200",
  };
  return (
    <Badge variant="outline" className={`rounded-sm text-[10px] ${toneMap[tone]}`}>
      {status || "unknown"}
    </Badge>
  );
}

export default function KeyPaySuperPaymentsPage() {
  const { clientId } = useParams();
  const [batches, setBatches] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmBatch, setConfirmBatch] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/integrations/keypay/clients/${clientId}/super-payments`);
      setBatches(data.batches || []);
      setByStatus(data.by_status || {});
      setNote(data.note || null);
    } catch (e) { toast.error(errMsg(e, "Failed to load super payments")); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (batch) => {
    setDetail({ loading: true, batch });
    try {
      const { data } = await api.get(
        `/integrations/keypay/clients/${clientId}/super-payments/${batch.id}`);
      setDetail({ loading: false, batch: data.batch || batch,
                  payments: data.payments || [], raw: data.raw });
    } catch (e) {
      toast.error(errMsg(e, "Failed to load batch detail"));
      setDetail(null);
    }
  };

  const submitBatch = async () => {
    if (!confirmBatch) return;
    setSubmitting(true);
    try {
      await api.post(
        `/integrations/keypay/clients/${clientId}/super-payments/${confirmBatch.id}/submit`);
      toast.success("Super batch submitted for processing");
      setConfirmBatch(null);
      await load();
    } catch (e) { toast.error(errMsg(e, "Submit failed")); }
    finally { setSubmitting(false); }
  };

  const summary = useMemo(() => {
    let total = 0;
    let pendingCount = 0;
    let completed = 0;
    for (const b of batches) {
      total += Number(b.total_amount) || 0;
      const s = String(b.status || "").toLowerCase();
      if (["pending", "awaitingapproval", "new"].includes(s.replace(/[^a-z]/g, ""))) {
        pendingCount += 1;
      } else if (["completed", "paid", "processed"].includes(s.replace(/[^a-z]/g, ""))) {
        completed += 1;
      }
    }
    return { total, pendingCount, completed };
  }, [batches]);

  return (
    <div className="p-6 max-w-7xl" data-testid="keypay-super-page">
      <Link to={`/integrations/keypay/clients/${clientId}`}
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="keypay-super-back">
        <ArrowLeft className="h-3 w-3" /> Back to dashboard
      </Link>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-emerald-100 text-emerald-700">
          <Wallet className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-overline">KEYPAY · SUPER PAYMENTS</div>
          <h1 className="font-display font-extrabold text-2xl tracking-tight">
            Super payment batches
          </h1>
          <div className="text-xs text-zinc-500">
            List, review status, and submit (approve) super batches for processing.
          </div>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={load} disabled={loading} data-testid="keypay-super-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                   : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {note && (
        <div className="mb-4 rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm"
          data-testid="keypay-super-note">
          {note}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Kpi label="Batches" value={batches.length} testid="keypay-super-kpi-count" />
        <Kpi label="Pending" value={summary.pendingCount}
          accent={summary.pendingCount > 0 ? "text-amber-700" : ""}
          testid="keypay-super-kpi-pending" />
        <Kpi label="Completed" value={summary.completed}
          accent="text-emerald-700" testid="keypay-super-kpi-completed" />
        <Kpi label="Total value" value={fmtMoney(summary.total)}
          testid="keypay-super-kpi-total" />
      </div>

      {/* Status roll-up */}
      {Object.keys(byStatus).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(byStatus).map(([s, n]) => (
            <div key={s} className="text-xs">
              <StatusBadge status={s} />
              <span className="ml-1 text-zinc-500">× {n}</span>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-0">
          <div className="grid grid-cols-[60px_120px_140px_1fr_130px_110px_120px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
            <span>Status</span>
            <span>ID</span>
            <span>Created</span>
            <span>Reference</span>
            <span>Employees</span>
            <span className="text-right">Amount</span>
            <span />
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : batches.length === 0 ? (
            <div className="p-8 text-center text-sm italic text-zinc-500"
              data-testid="keypay-super-empty">
              No super payment batches found for this client.
            </div>
          ) : batches.map((b) => (
            <div key={b.id}
              className="grid grid-cols-[60px_120px_140px_1fr_130px_110px_120px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50/60"
              data-testid={`keypay-super-row-${b.id}`}>
              <StatusBadge status={b.status} />
              <span className="font-mono text-xs">#{b.id}</span>
              <span className="text-xs font-mono text-zinc-600">{fmtDate(b.date_created)}</span>
              <span className="truncate text-sm">
                {b.external_reference || <span className="italic text-zinc-400">no reference</span>}
                {b.period_start && (
                  <span className="text-[10px] text-zinc-500 ml-2">
                    · {fmtDate(b.period_start)} → {fmtDate(b.period_end)}
                  </span>
                )}
              </span>
              <span className="text-xs">
                {b.employee_count ?? "—"} employee(s)
              </span>
              <span className="text-right font-semibold tabular-nums">
                {b.total_amount != null ? fmtMoney(b.total_amount) : "—"}
              </span>
              <span className="flex gap-1 justify-end">
                <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]"
                  onClick={() => openDetail(b)}
                  data-testid={`keypay-super-detail-${b.id}`}>Details</Button>
                {["pending", "awaitingapproval", "new"].includes(
                  String(b.status || "").toLowerCase().replace(/[^a-z]/g, "")) && (
                  <Button size="sm" className="rounded-sm h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => setConfirmBatch(b)}
                    data-testid={`keypay-super-submit-${b.id}`}>
                    <PlayCircle className="h-3 w-3 mr-1" />Submit
                  </Button>
                )}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Submit confirmation dialog */}
      <Dialog open={!!confirmBatch} onOpenChange={(o) => !o && setConfirmBatch(null)}>
        <DialogContent data-testid="keypay-super-confirm">
          <DialogHeader>
            <DialogTitle>Submit super batch #{confirmBatch?.id}?</DialogTitle>
            <DialogDescription>
              This transmits the batch to ClickSuper for processing. Once
              submitted the batch status will change to <b>Submitted</b>,
              then <b>Completed</b> once payment clears. There is no undo.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1 rounded border bg-zinc-50 p-3">
            <div><b>Reference:</b> {confirmBatch?.external_reference || "—"}</div>
            <div><b>Employees:</b> {confirmBatch?.employee_count || "—"}</div>
            <div><b>Amount:</b> {confirmBatch?.total_amount != null ? fmtMoney(confirmBatch.total_amount) : "—"}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBatch(null)}
              disabled={submitting}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700"
              onClick={submitBatch} disabled={submitting}
              data-testid="keypay-super-confirm-btn">
              {submitting
                ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                : <PlayCircle className="h-4 w-4 mr-1" />}
              Submit for processing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="keypay-super-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              Super batch #{detail?.batch?.id} · <StatusBadge status={detail?.batch?.status} />
            </DialogTitle>
          </DialogHeader>
          {detail?.loading ? (
            <div className="p-6 text-center">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…
            </div>
          ) : detail ? (
            <>
              <div className="grid md:grid-cols-2 gap-2 text-sm">
                <div><b>Reference:</b> {detail.batch?.external_reference || "—"}</div>
                <div><b>Created:</b> {fmtDate(detail.batch?.date_created)}</div>
                <div><b>Submitted:</b> {fmtDate(detail.batch?.date_submitted)}</div>
                <div><b>Paid:</b> {fmtDate(detail.batch?.date_paid)}</div>
                <div><b>Period:</b> {fmtDate(detail.batch?.period_start)} → {fmtDate(detail.batch?.period_end)}</div>
                <div><b>Amount:</b> {detail.batch?.total_amount != null ? fmtMoney(detail.batch.total_amount) : "—"}</div>
              </div>
              {detail.payments?.length > 0 && (
                <div className="mt-3">
                  <div className="font-semibold text-sm mb-1">Employee payments ({detail.payments.length})</div>
                  <div className="rounded border max-h-[300px] overflow-y-auto">
                    <table className="w-full text-xs" data-testid="keypay-super-payments-table">
                      <thead className="bg-zinc-50 border-b">
                        <tr className="text-left">
                          <th className="p-2">Employee</th>
                          <th className="p-2">Fund</th>
                          <th className="p-2">Status</th>
                          <th className="p-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.payments.map((p, i) => (
                          <tr key={i} className="border-b last:border-b-0">
                            <td className="p-2">{p.employeeName || p.EmployeeName || "—"}</td>
                            <td className="p-2 text-zinc-600">{p.superFundName || p.SuperFundName || "—"}</td>
                            <td className="p-2"><StatusBadge status={p.status || p.Status} /></td>
                            <td className="p-2 text-right tabular-nums">
                              {fmtMoney(p.amount || p.Amount || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white" data-testid={testid}>
      <div className="label-overline text-[9px]">{label}</div>
      <div className={`font-display font-bold text-xl mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}
